import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribeToJob } from '../../services/syncApi';
import { subscribeToEpicorJob } from '../../services/epicorApi';

const TERMINAL = ['succeeded', 'failed', 'cancelled'];

export const isTerminal = (status) => TERMINAL.includes(status);

const EMPTY = { id: null, kind: null, status: null, counters: {}, stagesCompleted: [] };

/**
 * Tracks a single job's live progress over Server-Sent Events.
 *
 * `track(id, kind, opts)` (re)binds to a job. `kind === 'product'` and
 * `kind === 'pricing'` (price list push) use the BigCommerce sync stream;
 * every other kind (customer push, ship-to pull, address push, single-entity
 * pull/push) is an Epicor-queue job and uses the Epicor stream. The SSE stream emits a final
 * `progress` event carrying the terminal status before it closes, so we detect
 * completion in the progress handler (more reliable than waiting for onClose)
 * and fire `opts.onTerminal(payload)` exactly once. This is what lets the
 * Product Changes action chain a compare job into a mutate job.
 */
export function useJobProgress() {
  const [state, setState] = useState(EMPTY);
  const unsubRef = useRef(null);
  const terminalFiredRef = useRef(false);

  const stop = useCallback(() => {
    if (unsubRef.current) {
      unsubRef.current();
      unsubRef.current = null;
    }
  }, []);

  const track = useCallback(
    (id, kind, opts = {}) => {
      stop();
      terminalFiredRef.current = false;
      setState({ id, kind, status: 'pending', counters: {}, stagesCompleted: [] });
      const subscribe = kind === 'product' || kind === 'pricing' ? subscribeToJob : subscribeToEpicorJob;
      unsubRef.current = subscribe(
        id,
        (payload) => {
          setState((prev) => ({
            ...prev,
            id,
            kind,
            status: payload.status ?? prev.status,
            counters: payload.counters ?? prev.counters,
            stagesCompleted: payload.stages_completed ?? prev.stagesCompleted,
          }));
          if (!terminalFiredRef.current && isTerminal(payload.status)) {
            terminalFiredRef.current = true;
            if (opts.onTerminal) opts.onTerminal(payload);
          }
        },
        () => {
          if (opts.onClose) opts.onClose();
        },
      );
    },
    [stop],
  );

  const reset = useCallback(() => {
    stop();
    terminalFiredRef.current = false;
    setState(EMPTY);
  }, [stop]);

  useEffect(() => stop, [stop]);

  return { ...state, track, stop, reset };
}

export default useJobProgress;
